// Read the visitor's choice from HubSpot's consent banner. Only a choice recorded in HubSpot's own cookie
// counts: on a page no banner covers, HubSpot reports every category as accepted but writes no cookie.
// The read functions take cookies and window as parameters so tests run without a browser.

export const HUBSPOT_CONSENT_COOKIE = '__hs_cookie_cat_pref';

export type ConsentChoice = {
  analytics: boolean;
  advertisement: boolean;
  functionality: boolean;
};

// HubSpot writes "1:true_2:false_3:true": 1 analytics, 2 advertisement, 3 functionality
const CATEGORY_KEYS: Record<string, keyof ConsentChoice> = {
  '1': 'analytics',
  '2': 'advertisement',
  '3': 'functionality',
};

export type HubSpotWindow = { _hsp?: unknown[] };

function currentWindow(): HubSpotWindow | undefined {
  return typeof window === 'undefined' ? undefined : (window as HubSpotWindow);
}

/** Queues commands for HubSpot's script, which reads the queue on load and takes over its push afterwards. */
export function pushToHubSpot(win: HubSpotWindow, ...commands: unknown[][]): void {
  const queue = win._hsp ?? [];
  queue.push(...commands);
  if (!win._hsp) {
    Object.assign(win, { _hsp: queue });
  }
}

function currentCookies(): string {
  try {
    return typeof document === 'undefined' ? '' : document.cookie;
  } catch {
    return '';
  }
}

export function readCookie(cookies: string, name: string): string | undefined {
  const match = cookies.split(';').find((part) => part.trim().startsWith(`${name}=`));
  return match?.trim().slice(name.length + 1) || undefined;
}

// tools set their cookies on the root domain; also clears a host only copy (local, preview hosts)
export function deleteCookie(name: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=; Max-Age=0; path=/; domain=.pfida.com`;
  document.cookie = `${name}=; Max-Age=0; path=/`;
}

/** The recorded choice, or null when there is none or it is not exactly HubSpot's three categories. */
export function readConsentChoice(cookies: string = currentCookies()): ConsentChoice | null {
  try {
    const raw = readCookie(cookies, HUBSPOT_CONSENT_COOKIE);
    if (!raw) {
      return null;
    }
    const value = decodeURIComponent(raw);
    // HubSpot also reads a comma separated form, so accept both
    const pairs = value.split(value.includes(',') ? ',' : '_').map((pair) => pair.split(':'));
    const wellFormed = pairs.every(
      ([key, flag, ...rest]) =>
        key !== undefined && Object.prototype.hasOwnProperty.call(CATEGORY_KEYS, key) && rest.length === 0 && (flag === 'true' || flag === 'false')
    );
    const categories = new Set(pairs.map(([key]) => key));
    if (!wellFormed || pairs.length !== categories.size || categories.size !== Object.keys(CATEGORY_KEYS).length) {
      return null;
    }
    return Object.fromEntries(
      pairs.map(([key, flag]) => [CATEGORY_KEYS[key as string], flag === 'true'])
    ) as ConsentChoice;
  } catch {
    return null;
  }
}

/** True once the visitor has answered the banner, either way. */
export function hasAnsweredConsent(cookies: string = currentCookies()): boolean {
  return readConsentChoice(cookies) !== null;
}

export function hasAdvertisingConsent(cookies: string = currentCookies()): boolean {
  return readConsentChoice(cookies)?.advertisement === true;
}

export function hasAnalyticsConsent(cookies: string = currentCookies()): boolean {
  return readConsentChoice(cookies)?.analytics === true;
}

export function hasFunctionalityConsent(cookies: string = currentCookies()): boolean {
  return readConsentChoice(cookies)?.functionality === true;
}

/**
 * Calls back with the advertising choice whenever HubSpot reports one, re-read from the cookie.
 * HubSpot has no way to remove a listener, so the returned unsubscribe silences it instead.
 */
export function onConsentChange(
  cb: (advertising: boolean) => void,
  win: HubSpotWindow | undefined = currentWindow(),
  cookies: () => string = currentCookies
): () => void {
  if (!win) {
    return () => {};
  }
  let subscribed = true;
  const listener = () => {
    if (subscribed) {
      cb(hasAdvertisingConsent(cookies()));
    }
  };
  pushToHubSpot(win, ['addPrivacyConsentListener', listener]);
  return () => {
    subscribed = false;
  };
}
